import React, { useState, useEffect } from 'react';
import { 
  UsersIcon, 
  DocumentDuplicateIcon, 
  ClockIcon,
  ArrowPathIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import api from '../../utils/api';
import ChartComponent from '../../components/charts/ChartComponent';
import DateRangeSelector from '../../components/analytics/DateRangeSelector';

const AnalyticsDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState({
    stats: [{
      name: 'Total Users',
      value: '0',
      change: '0%'
    }],
    caseDistribution: { 
      labels: [], 
      datasets: [{ 
        label: 'Cases', 
        data: [],
        backgroundColor: [
          'rgba(239, 68, 68, 0.5)',
          'rgba(16, 185, 129, 0.5)',
          'rgba(59, 130, 246, 0.5)',
          'rgba(139, 92, 246, 0.5)'
        ],
      }] 
    },
    billingData: { 
      labels: [], 
      datasets: [{ 
        label: 'Billing', 
        data: [],
        backgroundColor: 'rgba(139, 92, 246, 0.5)',
        borderColor: 'rgb(139, 92, 246)',
      }] 
    },
    timeTracking: { 
      labels: [], 
      datasets: [{ 
        label: 'Time', 
        data: [],
        backgroundColor: 'rgba(245, 158, 11, 0.5)',
        borderColor: 'rgb(245, 158, 11)',
      }] 
    }
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Date range state
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  // Only fetch data on mount and when date range changes
  useEffect(() => {
    let cancelled = false;
    
    const loadData = async () => {
      try {
        setLoading(true);
        setRefreshing(true);
        
        // Build query params
        const params = new URLSearchParams();
        params.append('start_date', startDate);
        params.append('end_date', endDate);
        
        // Fetch analytics data from multiple endpoints
        const response = await api.get('/api/analytics/stats/');
        
        if (cancelled) return; // Exit if component unmounted
        
        const analyticsResponse = response.data || response;
        
        // Handle both array and object response formats
        let formattedStats = [];
        if (Array.isArray(analyticsResponse)) {
          formattedStats = analyticsResponse;
        } else if (typeof analyticsResponse === 'object') {
          // Convert object format to array format
          formattedStats = [
            {
              name: 'Active Cases',
              value: String(analyticsResponse.activeCases || 0),
              change: '+0%'
            },
            {
              name: 'Billable Hours',
              value: `${analyticsResponse.billableHours || 0} hrs`,
              change: '+0%'
            },
            {
              name: 'Total Clients',
              value: String(analyticsResponse.clients || 0),
              change: '+0%'
            }
          ];
        }
        
        try {
          // Fetch all data concurrently with error handling for each request
          const results = await Promise.allSettled([
            api.get('/api/analytics/case-distribution/'),
            api.get('/api/analytics/billing/'),
            api.get('/api/analytics/time-tracking/')
          ]);
    
          if (cancelled) return; // Exit if component unmounted
    
          const defaultChartData = { labels: [], datasets: [] };
    
          // Process results and handle any individual failures
          const [caseDistribution, billingData, timeTracking] = results.map(
            result => {
              if (result.status === 'fulfilled' && result.value) {
                // Extract data from response
                const data = result.value.data || result.value;
                return data;
              }
              return defaultChartData;
            }
          );
          
          setAnalyticsData({
            stats: formattedStats,
            caseDistribution,
            billingData,
            timeTracking
          });
        } catch (chartErr) {
          if (cancelled) return;
          console.error('Failed to fetch chart data:', chartErr);
          // Still update the stats even if chart data fails
          setAnalyticsData(prevData => ({
            ...prevData,
            stats: formattedStats
          }));
        }
        
        if (!cancelled) {
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch analytics data:', err);
          setError('Failed to load analytics data. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setRefreshing(false);
          setLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, refreshKey]);

  // Handle date range selection
  const handleRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  if (loading && !refreshing) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error && !analyticsData.stats.length) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500 text-lg">{error}</p>
      </div>
    );
  }

  // Map stats to their icons and colors
  const statsWithIcons = analyticsData.stats.map(stat => {
    const icons = {
      'Total Users': UsersIcon,
      'Active Users (24h)': UsersIcon,
      'Documents Processed': DocumentDuplicateIcon,
      'Avg. API Response Time': ClockIcon
    };

    const colors = {
      'Total Users': 'bg-blue-500',
      'Active Users (24h)': 'bg-green-500',
      'Documents Processed': 'bg-indigo-500',
      'Avg. API Response Time': 'bg-yellow-500'
    };
    
    return {
      ...stat,
      icon: icons[stat.name] || UsersIcon,
      color: colors[stat.name] || 'bg-gray-500'
    };
  });

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Analytics Dashboard</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            View system usage, performance metrics, and user activity.
          </p>
        </div>
        
        {/* Date Range Selector */}
        <DateRangeSelector 
          startDate={startDate} 
          endDate={endDate} 
          onRangeChange={handleRangeChange} 
        />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statsWithIcons.map((stat) => (
          <div key={stat.name} className="bg-white dark:bg-gray-800 overflow-hidden shadow dark:shadow-gray-700/10 rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className={`flex-shrink-0 rounded-md ${stat.color} p-3`}>
                  <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{stat.name}</dt>
                    <dd className="text-2xl font-semibold text-gray-900 dark:text-white">{stat.value}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart Placeholders */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <BriefcaseIcon className="h-5 w-5 mr-2 text-primary-500" /> Case Distribution by Type
          </h3>
          <ChartComponent 
            title="Case Distribution" 
            type="pie" 
            data={analyticsData.caseDistribution} 
          />
        </div>

        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2 text-primary-500" /> Time Tracking by Attorney
          </h3>
          <ChartComponent 
            title="Time Tracking" 
            type="bar" 
            data={analyticsData.timeTracking} 
          />
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Data updated: {new Date().toLocaleString()}
          {error && <span className="ml-2 text-red-500">{error}</span>}
        </div>
        <button 
          onClick={() => {
            setError(null);
            setRefreshKey(k => k + 1);
          }}
          disabled={loading}
          className={`inline-flex items-center px-3 py-2 border rounded-md shadow-sm text-sm font-medium ${
            loading 
              ? 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed' 
              : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <ArrowPathIcon className={`-ml-1 mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
